import {useCallback, useEffect, useRef} from 'react'
import {useSelector} from 'react-redux'
import {getIsRefetchingOffers} from 'modules/sapiSearch/selectors'

import {useToastController} from '@vio/atlas/context/toastController'
import {Icon} from '@vio/atlas/Icon'
import {Loader} from '@vio/atlas/Loader'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'

const MIN_LOADING_DURATION_MS = 1000

const messages = {
  refreshing: {
    id: 'autoRefresh.refreshing',
    defaultMessage: 'Refreshing results...'
  },
  success: {
    id: 'autoRefresh.refreshed',
    defaultMessage: 'Price and availability refreshed'
  }
}

const toastStyle = {width: 'fit-content', minWidth: 'auto'}

// Added as part of feature-srp-refresh-price
export const useSearchPageRefreshToast = () => {
  const toast = useToastController()
  const toastRef = useRef(toast)
  toastRef.current = toast

  const isRefreshing = useSelector(getIsRefetchingOffers)

  const loadingToastIdRef = useRef<string | null>(null)
  const loadingStartTimeRef = useRef<number | null>(null)
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const prevIsRefreshingRef = useRef<boolean | undefined>(undefined)
  const hasRefreshFailedRef = useRef(false)

  const clearPendingTimeout = useCallback(() => {
    if (timeoutIdRef.current !== null) {
      clearTimeout(timeoutIdRef.current)
      timeoutIdRef.current = null
    }
  }, [])

  const closeLoadingToast = useCallback(() => {
    if (loadingToastIdRef.current !== null) {
      toast.close(loadingToastIdRef.current)
      loadingToastIdRef.current = null
    }
    loadingStartTimeRef.current = null
  }, [toast])

  const showLoading = useCallback(() => {
    closeLoadingToast()
    clearPendingTimeout()

    loadingStartTimeRef.current = Date.now()
    loadingToastIdRef.current = toast.open(
      <FormattedMessageWrapper {...messages.refreshing} />,
      {
        icon: <Loader variant="inverse" />,
        style: toastStyle
      }
    )
  }, [toast, closeLoadingToast, clearPendingTimeout])

  const showSuccess = useCallback(() => {
    // Prevent multiple success toasts from being scheduled
    clearPendingTimeout()

    const startTime = loadingStartTimeRef.current

    if (startTime === null) {
      toast.open(<FormattedMessageWrapper {...messages.success} />, {
        icon: <Icon name="CheckCircle" />,
        style: toastStyle
      })
      return
    }

    const elapsed = Date.now() - startTime
    const remaining = MIN_LOADING_DURATION_MS - elapsed

    const displaySuccess = () => {
      closeLoadingToast()
      toast.open(<FormattedMessageWrapper {...messages.success} />, {
        icon: <Icon name="CheckCircle" />,
        style: toastStyle
      })
      timeoutIdRef.current = null
    }

    if (remaining > 0) {
      timeoutIdRef.current = setTimeout(displaySuccess, remaining)
    } else {
      displaySuccess()
    }
  }, [toast, closeLoadingToast, clearPendingTimeout])

  // Call before the refresh flag clears, so a failure doesn't say "refreshed"
  const closeFailedRefresh = useCallback(() => {
    hasRefreshFailedRef.current = true
    clearPendingTimeout()
    closeLoadingToast()
  }, [clearPendingTimeout, closeLoadingToast])

  useEffect(() => {
    return () => {
      if (timeoutIdRef.current !== null) {
        clearTimeout(timeoutIdRef.current)
      }
      if (loadingToastIdRef.current !== null) {
        toastRef.current.close(loadingToastIdRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const wasRefreshing = prevIsRefreshingRef.current

    if (wasRefreshing === false && isRefreshing) {
      showLoading()
    }

    if (wasRefreshing && !isRefreshing) {
      if (!hasRefreshFailedRef.current) showSuccess()
      hasRefreshFailedRef.current = false
    }

    prevIsRefreshingRef.current = isRefreshing
  }, [isRefreshing, showLoading, showSuccess])

  return {closeFailedRefresh}
}
